import { useCallback } from 'react';
import { Button, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { TipAlert } from '../common/TipAlert';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { FormCheckbox, NumericInput, SmallButton } from '../common/forms/FormElements';
import { Modal } from '../common/Modal';


export const USCountyGroupPayRateGrid = ({
    workTypePayRates,
    contractUnionWorkTypes,
    userGroups,
    onPayRateChange,
    onSaturdayOTChange,
    onAddWorkTypePayRate,
    onRemoveWorkTypePayRate,
}) => {
    const handleSubmit = useCallback((newPayRate) => {
        const exists = workTypePayRates.some((x) =>
            x.contractUnionWorkTypeId == newPayRate.contractUnionWorkTypeId &&
            x.userGroupId == newPayRate.userGroupId,
        );

        if (exists) {
            toast.error('Duplicate pay rate: this work type already has a rate for the selected user group.');
            return;
        }

        onAddWorkTypePayRate(newPayRate);
    }, [workTypePayRates, onAddWorkTypePayRate]);


    const handleAddWorkTypePayRate = useCallback(() => {
        Modal.show({
            title: 'Add Work Type Pay Rate for User Groups',
            onSubmit: (values) => handleSubmit(values),
            body: () => (
                <>
                    <TipAlert
                        label="Any employees with one of these user group will be paid out at this rate for any work performed in the selected work type."
                    />

                    <ValidatedSelect
                        id="contractUnionWorkTypeId"
                        name="contractUnionWorkTypeId"
                        required
                        options={contractUnionWorkTypes.map((wt) => ({ value: wt.id, label: wt.description }))}
                        error="A work type selection is required."
                        label="Work Type"
                    />

                    <ValidatedSelect
                        id="userGroupId"
                        name="userGroupId"
                        required
                        options={userGroups.map((ug) => ({ value: ug.id, label: ug.groupName }))}
                        error="A user group selection is required."
                        label="User Group"
                    />

                    <NumericInput
                        id="payRate"
                        name="payRate"
                        label="Pay Rate"
                        required
                        step={0.01}
                        min={0}
                        error="A pay rate is required."
                    />

                    <NumericInput
                        id="fringeRate"
                        name="fringeRate"
                        label="Fringe Rate"
                        required
                        step={0.01}
                        min={0}
                        error="A fringe pay rate is required."
                    />

                    <FormCheckbox
                        id="alwaysHasSaturdayOvertime"
                        name="alwaysHasSaturdayOvertime"
                        labelText="Always Pays Saturday OT"
                    />
                </>
            ),
        });
    }, [handleSubmit, contractUnionWorkTypes, userGroups]);

    return (
        <>
            <div className="d-flex justify-content-end mb-1">
                <SmallButton
                    type="button"
                    onClick={handleAddWorkTypePayRate}
                >
                    <FontAwesomeIcon
                        icon={faPlusCircle}
                        size="md"
                        className="mr-2"
                    />
                    Add Pay Rate Per User Group
                </SmallButton>
            </div>
            <Table
                className="table table-sm"
                bordered
                responsive
            >
                <thead>
                    <tr>
                        <th>Work Type</th>
                        <th>User Group</th>
                        <th>Pay Rate</th>
                        <th>Fringe Rate</th>
                        <th className="text-center">Always Pays Saturday OT</th>
                        <th className="text-center">
                            <FontAwesomeIcon icon={faTrash} />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {workTypePayRates.map((payRate) => {
                        const workType = (contractUnionWorkTypes || []).find(
                            (w) => payRate.contractUnionWorkTypeId === w.id,
                        ) || {};
                        const groups = (userGroups || []).filter((ug) => payRate.userGroupId == ug.id).map((ug) => ug.groupName);

                        return (
                            <tr key={`${workType.id}${groups}`}>
                                <td className="align-middle">{workType.description || workType.name}</td>
                                <td className="align-middle">{groups}</td>
                                <td>
                                    <NumericInput
                                        id={`payRate-${workType.id}${groups}`}
                                        name={`payRate-${workType.id}${groups}`}
                                        value={payRate?.payRate || 0}
                                        onChange={(e) =>
                                            onPayRateChange(payRate, e.target.value, 'payRate')
                                        }
                                        type="number"
                                        step="0.01"
                                        placeholder="Enter Pay Rate"
                                        required
                                    />
                                </td>

                                <td>
                                    <NumericInput
                                        id={`fringeRate-${workType.id}${groups}`}
                                        name={`fringeRate-${workType.id}${groups}`}
                                        value={payRate?.fringeRate || 0}
                                        onChange={(e) =>
                                            onPayRateChange(payRate, e.target.value, 'fringeRate')
                                        }
                                        type="number"
                                        step="0.01"
                                        placeholder="Enter Fringe Rate"
                                        required
                                    />
                                </td>

                                <td className="text-center align-middle">
                                    <FormCheckbox
                                        id={`alwaysHasSaturdayOvertime-${workType.id}${groups}`}
                                        name={`alwaysHasSaturdayOvertime-${workType.id}${groups}`}
                                        checked={payRate?.alwaysHasSaturdayOvertime || false}
                                        onChange={(e) => onSaturdayOTChange(payRate, e.target.checked)}
                                    />
                                </td>
                                <td
                                    className="text-center"
                                    style={{ width: '30px' }}
                                >
                                    {!!payRate.userGroupId && (
                                        <Button
                                            type="button"
                                            color="outline-primary"
                                            onClick={() => onRemoveWorkTypePayRate(payRate)}
                                            size="sm"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </>
    );
};
